/**
 * useProfileCompleteness
 *
 * Single source of truth for "which parts of the athlete profile are still
 * empty" and "what's the severity of each gap." Drives the JIT prompt
 * surfaces (ProfileCompletionRouter on /profile, the dashboard banner, the
 * college-lists banner) — same data, different copy + section filter per
 * surface.
 *
 * Severity tiers:
 *   - 'recommended' fields drive matched-for-you scoring or coach email
 *     signatures. These are what we surface in the JIT.
 *   - 'nice_to_have' fields are cosmetic (awards, achievements, etc.). They
 *     stay reachable via the manual edit modals but never appear in JIT
 *     prompts.
 *
 * Sections map 1:1 to the existing edit modals on PlayerProfile.tsx, so the
 * JIT prompt is purely a router that opens whichever modal owns the gap.
 *
 * Spec: see "Phase 2.5 — JIT routing through existing edit modals" in
 * /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md.
 */

import { useMemo } from 'react';

export type SectionKey = 'personal' | 'athletic' | 'performance' | 'team' | 'academic' | 'image';
export type Severity = 'recommended' | 'nice_to_have';

export interface AthleteSnapshot {
  id?: string;
  // Personal
  date_of_birth?: string | null;
  city?: string | null;
  phone?: string | null;
  ncaa_id?: string | null;
  // Athletic. height has two valid shapes: int inches (used by PlayerProfile
  // after parseHeightToInches) or the raw string from athlete_sport_info
  // (e.g. "5'9\""). Either non-empty value counts as filled.
  height_inches?: number | null;
  height?: string | null;
  weight_lbs?: number | null;
  weight?: number | null;
  position?: string | null;
  positions?: string[] | null;
  bats?: string | null;
  throws?: string | null;
  awards?: string[] | null;
  // Academic
  current_gpa?: number | string | null;
  sat_total?: number | string | null;
  act_total?: number | string | null;
  // Team — accept both shapes. The /families/me/my-athletes payload
  // delivers school_info.name + travel_club_info.name (nested), while
  // older callers used flat school_name + travel_club_name. Either is
  // valid; the audit below treats them as equivalent.
  school_name?: string | null;
  travel_club_name?: string | null;
  school_info?: { name?: string | null } | null;
  travel_club_info?: { name?: string | null } | null;
  // Performance — likewise, the perf modal hands back fields wrapped in
  // a `stats` key, but matched_for_you and the rest of the codebase
  // read them as performance_metrics. Audit reads from either.
  performance_metrics?: {
    exit_velocity?: number | null;
    overhand_velocity?: number | null;
    home_to_first?: number | null;
  } | null;
  stats?: {
    exit_velocity?: number | null;
    overhand_velocity?: number | null;
    home_to_first?: number | null;
    home_to_1st?: number | null;
  } | null;
  // Image. profile_picture_filename is the real source of truth — the _url
  // column on athlete_profiles is a legacy field that no upload path writes
  // to. We keep _url accepted for backward compat with callers that still
  // pass the old shape, but the check below treats either as filled.
  profile_picture_url?: string | null;
  profile_picture_filename?: string | null;
}

export interface SectionAudit {
  key: SectionKey;
  label: string;
  missingRecommended: string[]; // field labels for UI display
  missingAll: string[];
  severity: Severity;
}

export interface ProfileCompletenessResult {
  isComplete: boolean;
  recommendedComplete: boolean;
  sections: Record<SectionKey, SectionAudit>;
  // Convenience: ordered list of sections that have at least one
  // recommended-tier gap. Use this to drive JIT row rendering.
  sectionsWithRecommendedGaps: SectionAudit[];
}

const SECTION_META: Record<SectionKey, { label: string }> = {
  personal: { label: 'Personal Info' },
  athletic: { label: 'Athletic Info' },
  performance: { label: 'Performance Metrics' },
  team: { label: 'Team Info' },
  academic: { label: 'Academic Info' },
  image: { label: 'Profile Photo' },
};

// matched_for_you's athlete_score only consults pitch-specific benchmarks
// for these positions; fivetran_dropbox.performance_benchmarks_table has
// min_exit_velocity_mph for pitchers but no overhand / home-to-1st rows,
// so those metrics never contribute to the score for pitchers.
const PITCHER_POSITIONS = new Set<string>([
  'Pitcher',
  'Relief Pitcher',
  'Starting Pitcher',
  'Starter/Reliever',
]);

/**
 * Build the audit. Pure function (deterministic given input snapshot); the
 * thin React-hook wrapper below just memoizes it. Surfaces that consume an
 * already-memoized athlete object can call this directly instead.
 */
export function auditProfileCompleteness(a: AthleteSnapshot | null | undefined): ProfileCompletenessResult {
  const safe: AthleteSnapshot = a || {};

  // --- Personal ---
  // dob and city are recommended. Phone is cosmetic (it appears in the
  // coach-email signature when present but isn't required for matched-for-you
  // scoring), so it lives in missingAll only — same tier as NCAA ID.
  const personalMissingRec: string[] = [];
  if (!safe.date_of_birth) personalMissingRec.push('Date of birth');
  if (!safe.city) personalMissingRec.push('City');
  const personalMissingAll = [...personalMissingRec];
  if (!safe.phone) personalMissingAll.push('Phone');
  if (!safe.ncaa_id) personalMissingAll.push('NCAA ID');

  // --- Athletic ---
  // height/weight are recommended (athlete_score in matched-for-you uses
  // performance_benchmarks against position; height/weight aren't in the
  // current scoring function but they're universally shown to coaches).
  // bats/throws/awards are nice-to-have. Multi-position select is
  // nice-to-have for now — quickstart's single position drives matching.
  const athleticMissingRec: string[] = [];
  const hasHeight = !!safe.height_inches || !!(safe.height && String(safe.height).trim());
  const hasWeight = !!safe.weight_lbs || !!safe.weight;
  if (!hasHeight) athleticMissingRec.push('Height');
  if (!hasWeight) athleticMissingRec.push('Weight');
  const athleticMissingAll = [...athleticMissingRec];
  if (!safe.bats) athleticMissingAll.push('Bats');
  if (!safe.throws) athleticMissingAll.push('Throws');
  if (!safe.awards || safe.awards.length === 0) athleticMissingAll.push('Awards');
  // Multi-position: only nag if there's literally no position at all (which
  // shouldn't happen post-quickstart). Adding more positions stays a manual
  // edit.
  if (!safe.position && (!safe.positions || safe.positions.length === 0)) {
    athleticMissingRec.push('Position');
    athleticMissingAll.unshift('Position');
  }

  // --- Performance ---
  // matched_for_you's athlete_score averages exit_velocity, overhand_velocity,
  // and home_to_first against per-position rows in
  // fivetran_dropbox.performance_benchmarks_table. Pitchers only have a
  // min_exit_velocity_mph benchmark in that table — overhand and home-to-1st
  // never contribute to a pitcher's score, so we don't nag them for those.
  //
  // Read from `performance_metrics` (canonical) OR `stats` (what the
  // perf-metrics edit modal hands back) so the pill stays in sync after a
  // local save without a full refetch. stats also uses home_to_1st as a
  // legacy alias for home_to_first.
  const pm = safe.performance_metrics || {};
  const st = safe.stats || {};
  const exitVelo = pm.exit_velocity ?? st.exit_velocity;
  const overhandVelo = pm.overhand_velocity ?? st.overhand_velocity;
  const homeToFirst = pm.home_to_first ?? st.home_to_first ?? st.home_to_1st;
  const isPitcher = !!safe.position && PITCHER_POSITIONS.has(safe.position);
  const performanceMissingRec: string[] = [];
  if (!exitVelo) performanceMissingRec.push('Exit velocity');
  if (!isPitcher) {
    if (!overhandVelo) performanceMissingRec.push('Overhand velocity');
    if (!homeToFirst) performanceMissingRec.push('Home to first');
  }
  const performanceMissingAll = [...performanceMissingRec];
  // Keep these in missingAll for pitchers (they can still fill them; they
  // just don't drive the score) so the manual-edit completeness count is
  // accurate even though the JIT doesn't surface them.
  if (isPitcher) {
    if (!overhandVelo) performanceMissingAll.push('Overhand velocity');
    if (!homeToFirst) performanceMissingAll.push('Home to first');
  }

  // --- Team ---
  // Coach email signature reads HS or travel club, whichever is present.
  // Nag only when BOTH are empty. Accept either flat (`school_name`,
  // `travel_club_name`) or nested (`school_info.name`, `travel_club_info.name`)
  // shapes — the /families/me/my-athletes payload uses nested; older
  // callers used flat.
  const hasHS = !!(safe.school_name || safe.school_info?.name);
  const hasTravel = !!(safe.travel_club_name || safe.travel_club_info?.name);
  const teamMissingRec: string[] = [];
  if (!hasHS && !hasTravel) teamMissingRec.push('Team');
  const teamMissingAll = [...teamMissingRec];
  if (!hasHS && hasTravel) teamMissingAll.push('High school');
  if (hasHS && !hasTravel) teamMissingAll.push('Travel club');

  // --- Academic ---
  // matched_for_you's academic_score reads sat_total OR act_total (live
  // function api.matched_for_you on staging) — GPA is never consulted.
  // So GPA is cosmetic only: visible in the edit modal and counted in
  // missingAll, but never appears in JIT prompts. SAT and ACT are
  // interchangeable for scoring; nag only when both are absent.
  const hasSAT = safe.sat_total != null && safe.sat_total !== '';
  const hasACT = safe.act_total != null && safe.act_total !== '';
  const academicMissingRec: string[] = [];
  if (!hasSAT && !hasACT) academicMissingRec.push('SAT or ACT');
  const academicMissingAll = [...academicMissingRec];
  if (safe.current_gpa == null || safe.current_gpa === '') academicMissingAll.push('GPA');
  if (!hasSAT && hasACT) academicMissingAll.push('SAT total');
  if (hasSAT && !hasACT) academicMissingAll.push('ACT total');

  // --- Image ---
  // profile_picture_filename is what upload writes; _url is a dead column.
  // Either present = photo exists.
  //
  // Photo lives in missingAll only — never in missingRecommended. That keeps
  // it out of the "N items left on your profile" pill + ProfileGapBanner +
  // JIT router (all driven by missingRecommended), while still tracking it
  // for the absolute-complete check that recruiters care about. The pill
  // showing "1 missing" purely because the user hadn't uploaded a photo
  // was misleading — every other recommended field was filled in.
  const hasPhoto = !!(safe.profile_picture_filename || safe.profile_picture_url);
  const imageMissingRec: string[] = [];
  const imageMissingAll: string[] = [];
  if (!hasPhoto) imageMissingAll.push('Profile photo');

  const sections: Record<SectionKey, SectionAudit> = {
    personal: {
      key: 'personal',
      label: SECTION_META.personal.label,
      missingRecommended: personalMissingRec,
      missingAll: personalMissingAll,
      severity: personalMissingRec.length ? 'recommended' : 'nice_to_have',
    },
    athletic: {
      key: 'athletic',
      label: SECTION_META.athletic.label,
      missingRecommended: athleticMissingRec,
      missingAll: athleticMissingAll,
      severity: athleticMissingRec.length ? 'recommended' : 'nice_to_have',
    },
    performance: {
      key: 'performance',
      label: SECTION_META.performance.label,
      missingRecommended: performanceMissingRec,
      missingAll: performanceMissingAll,
      severity: performanceMissingRec.length ? 'recommended' : 'nice_to_have',
    },
    team: {
      key: 'team',
      label: SECTION_META.team.label,
      missingRecommended: teamMissingRec,
      missingAll: teamMissingAll,
      severity: teamMissingRec.length ? 'recommended' : 'nice_to_have',
    },
    academic: {
      key: 'academic',
      label: SECTION_META.academic.label,
      missingRecommended: academicMissingRec,
      missingAll: academicMissingAll,
      severity: academicMissingRec.length ? 'recommended' : 'nice_to_have',
    },
    image: {
      key: 'image',
      label: SECTION_META.image.label,
      missingRecommended: imageMissingRec,
      missingAll: imageMissingAll,
      severity: imageMissingRec.length ? 'recommended' : 'nice_to_have',
    },
  };

  const sectionsWithRecommendedGaps = (Object.values(sections) as SectionAudit[])
    .filter((s) => s.missingRecommended.length > 0);

  const recommendedComplete = sectionsWithRecommendedGaps.length === 0;
  const isComplete = (Object.values(sections) as SectionAudit[])
    .every((s) => s.missingAll.length === 0);

  return {
    isComplete,
    recommendedComplete,
    sections,
    sectionsWithRecommendedGaps,
  };
}

// Thin hook wrapper. Components that already memoize the snapshot can call
// auditProfileCompleteness() directly.
export function useProfileCompleteness(athlete: AthleteSnapshot | null | undefined): ProfileCompletenessResult {
  return useMemo(() => auditProfileCompleteness(athlete), [athlete]);
}
